import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { Link, useNavigate } from 'react-router-dom';
import useEmblaCarousel from 'embla-carousel-react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { portfolioProjects } from '../data/mock';
import useSEO from '../hooks/useSEO';
import { ROUTE_DEFAULTS, SITE_URL } from '../config/seo';

gsap.registerPlugin(ScrollTrigger);

// Fallback slide configs so the slider always has multiple slides (visible sliding when images missing)
const FALLBACK_SLIDES = [
  { gradient: 'from-violet-900/40 via-purple-900/25 to-blue-900/20', shape: 'rounded-2xl', size: 'w-28 h-28' },
  { gradient: 'from-blue-900/40 via-violet-900/25 to-purple-900/20', shape: 'rounded-full', size: 'w-32 h-32' },
  { gradient: 'from-purple-900/40 via-pink-900/20 to-violet-900/25', shape: 'rounded-xl', size: 'w-24 h-24' },
];

// Premium slider card: multiple images in a carousel with dots and optional arrows
const WorkCardSlider = ({ project, cardIndex, isHovered, accentColor }) => {
  const slides = project.images?.length ? project.images : (project.image ? [project.image] : []);
  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: true,
    align: 'start',
    duration: 22,
    skipSnaps: false,
  });
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [slideErrors, setSlideErrors] = useState({});

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setSelectedIndex(emblaApi.selectedScrollSnap());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    onSelect();
    emblaApi.on('select', onSelect);
    return () => emblaApi.off('select', onSelect);
  }, [emblaApi, onSelect]);

  const scrollPrev = (e) => { e.stopPropagation(); emblaApi?.scrollPrev(); };
  const scrollNext = (e) => { e.stopPropagation(); emblaApi?.scrollNext(); };
  const scrollTo = (e, i) => { e.stopPropagation(); emblaApi?.scrollTo(i); };

  const handleSlideImageError = (i) => {
    setSlideErrors((prev) => ({ ...prev, [i]: true }));
  };

  const hasAnyImage = slides.length > 0;
  // When single image is duplicated to 2 slides, only slideErrors[0] exists for the one source; check it explicitly
  const allSlidesFailed = hasAnyImage && (
    slides.length === 1 ? slideErrors[0] : slides.every((_, i) => slideErrors[i])
  );
  const showFallback = !hasAnyImage || allSlidesFailed;
  // When fallback: 3 slides so slider is always interactive. When real images: use as-is (duplicate single so sliding works)
  const effectiveSlides = showFallback ? [1, 2, 3] : (slides.length === 1 ? [slides[0], slides[0]] : slides);
  const slideCount = effectiveSlides.length;
  const showDotsAndArrows = slideCount > 1;

  // Auto-advance slides (effect after showDotsAndArrows is in scope)
  useEffect(() => {
    if (!emblaApi || !showDotsAndArrows) return;
    const interval = setInterval(() => {
      emblaApi.scrollNext();
    }, 4000);
    return () => clearInterval(interval);
  }, [emblaApi, showDotsAndArrows]);

  return (
    <motion.div
      whileHover={{ y: -10 }}
      transition={{ duration: 0.4, ease: 'easeOut' }}
      className="aspect-[4/3] mb-6 overflow-hidden relative rounded-2xl border border-white/10 bg-black/40 min-h-[240px]"
      style={{
        boxShadow: isHovered
          ? `0 24px 48px -12px rgba(0,0,0,0.5), 0 0 0 1px rgba(255,255,255,0.08), 0 0 40px rgba(${accentColor}, 0.25)`
          : '0 16px 40px -16px rgba(0,0,0,0.4), 0 0 0 1px rgba(255,255,255,0.05)',
      }}
    >
      <div className="absolute inset-0 rounded-2xl overflow-hidden min-h-0" ref={emblaRef}>
        <div className="flex h-full min-h-0" style={{ touchAction: 'pan-y pinch-zoom' }}>
          {showFallback ? (
            FALLBACK_SLIDES.map((config, i) => (
              <div
                key={i}
                className={`flex-[0_0_100%] min-w-0 h-full min-h-0 flex items-center justify-center bg-gradient-to-br ${config.gradient}`}
                style={{ boxShadow: `inset 0 0 80px rgba(${accentColor}, 0.08)` }}
              >
                <motion.div
                  animate={{ rotate: isHovered ? 180 : 0, scale: isHovered ? 1.1 : 1 }}
                  transition={{ duration: 0.6 }}
                  className={`${config.size} ${config.shape} border-2 border-white/20`}
                  style={{ boxShadow: `0 0 30px rgba(${accentColor}, 0.2)` }}
                />
              </div>
            ))
          ) : (
            effectiveSlides.map((src, i) => (
              <div key={i} className="flex-[0_0_100%] min-w-0 h-full min-h-0 relative">
                {slideErrors[i] ? (
                  <div
                    className="absolute inset-0 bg-gradient-to-br from-violet-900/25 via-purple-900/15 to-blue-900/25 flex items-center justify-center"
                    style={{ boxShadow: `inset 0 0 60px rgba(${accentColor}, 0.06)` }}
                  />
                ) : (
                  <img
                    src={src}
                    alt={`${project.title} – slide ${i + 1}`}
                    width={1600}
                    height={900}
                    className="w-full h-full min-h-0 object-cover object-center select-none block"
                    draggable={false}
                    loading="lazy"
                    decoding="async"
                    onError={() => handleSlideImageError(i)}
                  />
                )}
              </div>
            ))
          )}
        </div>
      </div>

      {/* Bottom gradient + dots */}
      <div
        className="absolute inset-x-0 bottom-0 h-20 bg-gradient-to-t from-black/70 via-black/30 to-transparent rounded-b-2xl pointer-events-none"
        aria-hidden
      />
      {showDotsAndArrows && (
        <div
          className="absolute bottom-3 left-0 right-0 flex items-center justify-center gap-1.5 pointer-events-auto z-10"
          onClick={(e) => e.stopPropagation()}
        >
          {effectiveSlides.map((_, i) => (
            <button
              key={i}
              type="button"
              aria-label={`Go to slide ${i + 1}`}
              onClick={(e) => scrollTo(e, i)}
              className="h-1.5 rounded-full transition-all duration-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/50"
              style={{
                width: selectedIndex === i ? 20 : 8,
                backgroundColor: selectedIndex === i ? 'rgba(255,255,255,0.95)' : 'rgba(255,255,255,0.35)',
              }}
            />
          ))}
        </div>
      )}

      {/* Hover arrows (premium) */}
      {showDotsAndArrows && (
        <>
          <button
            type="button"
            aria-label="Previous slide"
            onClick={scrollPrev}
            className="absolute left-2 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-black/50 backdrop-blur-sm border border-white/10 text-white/90 flex items-center justify-center opacity-0 group-hover/card:opacity-100 hover:bg-black/70 transition-all duration-200 z-10"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <button
            type="button"
            aria-label="Next slide"
            onClick={scrollNext}
            className="absolute right-2 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-black/50 backdrop-blur-sm border border-white/10 text-white/90 flex items-center justify-center opacity-0 group-hover/card:opacity-100 hover:bg-black/70 transition-all duration-200 z-10"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        </>
      )}

      {/* Hover overlay – View Project */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: isHovered ? 1 : 0 }}
        transition={{ duration: 0.25 }}
        className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent flex items-end justify-center pb-12 pointer-events-none rounded-2xl"
      >
        <span className="text-white/95 text-sm font-medium tracking-wide">View Project →</span>
      </motion.div>
    </motion.div>
  );
};

const Work = () => {
  const [hoveredProject, setHoveredProject] = useState(null);
  const navigate = useNavigate();

  useSEO({
    ...ROUTE_DEFAULTS['/portfolio'],
    jsonLd: {
      '@context': 'https://schema.org',
      '@type': 'CollectionPage',
      '@id': `${SITE_URL}/portfolio#webpage`,
      url: `${SITE_URL}/portfolio`,
      name: 'Portfolio — Cultiv AI Studio',
      description:
        'Case studies of AI-generated brand, video, and product work from Cultiv AI Studio.',
      isPartOf: { '@id': `${SITE_URL}/#website` },
      mainEntity: {
        '@type': 'ItemList',
        itemListElement: portfolioProjects.map((p, idx) => ({
          '@type': 'ListItem',
          position: idx + 1,
          url: `${SITE_URL}/portfolio/${p.id}`,
          name: p.title,
        })),
      },
    },
  });

  useEffect(() => {
    gsap.fromTo(
      '.work-card',
      { opacity: 0, y: 60 },
      {
        opacity: 1,
        y: 0,
        duration: 0.8,
        stagger: 0.15,
        ease: 'power3.out',
        scrollTrigger: {
          trigger: '.work-grid',
          start: 'top 80%'
        }
      }
    );
  }, []);

  const handleProjectClick = (projectId) => {
    navigate(`/portfolio/${projectId}`);
  };

  const accentForIndex = (i) => {
    const colors = ['139, 92, 246', '59, 130, 246', '236, 72, 153'];
    return colors[i % 3];
  };

  return (
    <div className="bg-black text-white min-h-screen pt-32 pb-20">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="mb-20 text-center"
        >
          <h1 className="md:text-8xl !font-light !text-6xl mb-6">Viral Case Studies</h1>
          <p className="text-xl text-white/60 max-w-2xl mx-auto">
            See how we saved brands 1000+ hours and millions in production costs.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 work-grid">
          {portfolioProjects.map((project, index) => (
            <div
              key={project.id}
              onClick={() => handleProjectClick(project.id)}
              onMouseEnter={() => setHoveredProject(project.id)}
              onMouseLeave={() => setHoveredProject(null)}
              className="work-card group/card cursor-pointer"
            >
              <WorkCardSlider
                project={project}
                cardIndex={index}
                isHovered={hoveredProject === project.id}
                accentColor={accentForIndex(index)}
              />

              <div>
                <h3 className="text-2xl font-light mb-2 group-hover:text-violet-400 transition-colors duration-300">
                  {project.title}
                </h3>
                <p className="text-violet-400/60 text-sm font-medium tracking-wide mb-2">{project.category}</p>
                <p className="text-white/60 text-sm leading-relaxed">{project.description}</p>
              </div>
            </div>
          ))}
        </div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="mt-32 text-center">

          <div className="p-12 border border-violet-500/20 rounded-2xl bg-gradient-to-br from-violet-900/10 to-transparent neon-border inline-block">
            <p className="text-xl text-white/60 mb-8">Have a project in mind?</p>
            <Link to="/contact" title="Book a free AI creative strategy call">
              <motion.button
                whileHover={{ scale: 1.05, y: -2 }}
                whileTap={{ scale: 0.95 }}
                className="bg-white text-black px-12 py-5 rounded-xl text-sm tracking-wide hover:bg-white/90 transition-colors duration-300"
                style={{
                  boxShadow: '0 0 30px rgba(255, 255, 255, 0.2)'
                }}>

                Book a free strategy call
              </motion.button>
            </Link>
          </div>

          {/* Secondary cross-links for internal discoverability + SEO anchor diversification */}
          <div className="mt-10 flex flex-wrap items-center justify-center gap-x-6 gap-y-3 text-sm text-white/60">
            <span className="text-white/40">Explore more:</span>
            <Link
              to="/ai-creatives"
              title="Browse the AI creatives gallery"
              className="underline decoration-violet-400/40 underline-offset-4 hover:text-violet-300 hover:decoration-violet-300 transition-colors"
            >
              Browse AI creatives gallery
            </Link>
            <span className="text-white/20">•</span>
            <Link
              to="/"
              title="Cultiv AI Studio — AI creative agency home"
              className="underline decoration-violet-400/40 underline-offset-4 hover:text-violet-300 hover:decoration-violet-300 transition-colors"
            >
              Back to AI creative agency home
            </Link>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Work;